// Provisional deal overlay (spec 2026-10-06): apply today's executed-but-not-
// yet-reconciled deals from /subledger/balance-sheet `provisional` to the
// balance sheet. All prices are POUNDS per unit (server normalises pence).
import type { AssetPositionRow, ProvisionalDealEntry } from '../api/client'

// kind drives the cash maths in PositionBreakdown (never match on label text):
//   committed / bought — cash already sent to HL, taken out of the cash row until settled
//   selling / proceeds — sale tag before the strike, sale proceeds after it
export type ProvisionalLineKind = 'committed' | 'bought' | 'selling' | 'proceeds'
export interface ProvisionalLine { key: string; kind: ProvisionalLineKind; label: string; amountGbp: number; tag: string }

const priced = (en: ProvisionalDealEntry) => en.phase !== 'PreStrike'

function priceOf(en: ProvisionalDealEntry, liveNav: number | null | undefined): number | null {
  if (en.priceGbp != null) return en.priceGbp
  return en.priceSource === 'live_synth' && liveNav != null && liveNav > 0 ? liveNav : null
}

// Mark for a row the overlay touches: a held position keeps the server's mark
// (the same figure netPortfolioValue was built from); units bought from flat
// are marked at the live synth NAV so they tick from the strike.
function markFor(base: AssetPositionRow | null, liveNav: number | null | undefined, fallback: number): number {
  if (base && base.units > 0) return base.markPrice
  return liveNav != null && liveNav > 0 ? liveNav : fallback
}

function buyUnits(en: ProvisionalDealEntry, liveNav: number | null | undefined): number {
  if (en.units > 0) return en.units
  const p = priceOf(en, liveNav)
  return p != null && en.amountGbp != null ? en.amountGbp / p : 0
}

export function overlayPosition(
  base: AssetPositionRow | null, entries: ProvisionalDealEntry[], assetType: string, liveNavGbp: number | null,
): { position: AssetPositionRow | null; soldUnits: number; soldPriceGbp: number | null } {
  let units = base?.units ?? 0
  let cost = (base?.avgCost ?? 0) * units
  let soldUnits = 0
  let soldPriceGbp: number | null = null
  for (const en of entries) {
    if (en.assetType !== assetType || !priced(en)) continue
    if (en.side === 'SELL') {
      // No price yet (live synth unavailable): keep the holding so the rows
      // and the total stay consistent until a price exists.
      if (priceOf(en, liveNavGbp) == null) continue
      const u = Math.min(en.units, units)
      const avg = units > 0 ? cost / units : 0
      units -= u; cost -= avg * u
      soldUnits += u
      soldPriceGbp = priceOf(en, liveNavGbp)
    } else {
      const u = buyUnits(en, liveNavGbp)
      const p = priceOf(en, liveNavGbp)
      if (u <= 0 || p == null) continue
      units += u; cost += u * p
    }
  }
  if (units <= 1e-9) return { position: null, soldUnits, soldPriceGbp }
  const avgCost = cost / units
  const mark = markFor(base, liveNavGbp, avgCost)
  return {
    position: {
      assetType, description: base?.description ?? '', units, avgCost,
      markPrice: mark, markValue: units * mark, unrealisedPnl: (mark - avgCost) * units,
    },
    soldUnits, soldPriceGbp,
  }
}

function hhmmUk(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })
}

export function provisionalLines(
  entries: ProvisionalDealEntry[], liveNavByFund: Record<string, number | null>, _now: Date,
): ProvisionalLine[] {
  return entries.map(en => {
    const key = `${en.fund}-${en.side}`
    if (en.side === 'BUY') {
      // Pre-strike, or past it with no price yet (a fund without a strike anchor or a live
      // synth NAV — MINERS on its first BUY day, 2026-11-02): no units can be shown as a
      // position row, so the cash stays out as a committed line instead of vanishing.
      if (en.phase === 'PreStrike' || priceOf(en, liveNavByFund[en.fund]) == null)
        return { key, kind: 'committed', label: `Committed to ${en.fund}`, amountGbp: en.amountGbp ?? 0, tag: '' }
      return { key, kind: 'bought', label: `Bought ${en.fund}`, amountGbp: en.amountGbp ?? 0, tag: en.phase === 'Booked' ? 'booked' : 'est.' }
    }
    if (en.phase === 'PreStrike') {
      const label = en.origin === 'hl_manual'
        ? `${en.fund} — Sold in HL app, prices at ${hhmmUk(en.strikeUtc)}`
        : `${en.fund} — Selling at ${hhmmUk(en.strikeUtc)}`
      return { key, kind: 'selling', label, amountGbp: 0, tag: '' }
    }
    const p = priceOf(en, liveNavByFund[en.fund])
    const amount = en.amountGbp ?? (p != null ? en.units * p : 0)
    const booked = en.phase === 'Booked'
    return { key, kind: 'proceeds', label: `Sale proceeds (${booked ? 'booked' : 'est.'}) — ${en.fund}`, amountGbp: amount, tag: booked ? 'booked' : 'est.' }
  })
}

export function portfolioValueAdjustment(
  positions: AssetPositionRow[], entries: ProvisionalDealEntry[], liveNavByFund: Record<string, number | null>,
): number {
  let adj = 0
  for (const en of entries) {
    if (!priced(en)) continue
    const live = liveNavByFund[en.fund]
    if (en.side === 'SELL') {
      const p = priceOf(en, live)
      if (p == null) continue
      const mark = positions.find(x => x.assetType === en.assetType)?.markPrice ?? p
      adj += (en.amountGbp ?? en.units * p) - en.units * mark
    } else {
      const p = priceOf(en, live)
      if (p == null || en.amountGbp == null) continue
      const base = positions.find(x => x.assetType === en.assetType) ?? null
      adj += buyUnits(en, live) * markFor(base, live, p) - en.amountGbp
    }
  }
  return adj
}
